import { Devices } from '../../service/Devices';
import { TooltipPosition } from './interface';
import { clamp } from '@jds/react-engine/lib/helpers';

export function calculateOffsets(
  position: string,
  anchorRect: DOMRect,
  tooltipRect: DOMRect,
  gap: number
) {
  let offsetX = '';
  let offsetY = '';
  const halfAnchorWidth = anchorRect.width / 2;
  const halfAnchorHeight = anchorRect.height / 2;
  const halfTooltipWidth = tooltipRect.width / 2;
  const halfTooltipHeight = tooltipRect.height / 2;
  const tipSize = 24;
  const tipSpace = 4;
  const verticalCenterX = anchorRect.left + halfAnchorWidth - halfTooltipWidth;
  const verticalEndX = anchorRect.right - tooltipRect.width - tipSpace;
  const verticalStartX = anchorRect.left + halfAnchorWidth - tipSize + tipSpace;
  const horizontalCenterY =
    anchorRect.top + halfAnchorHeight - halfTooltipHeight;
  const horizontalEndY = anchorRect.bottom - tooltipRect.height - tipSpace;
  const horizontalStartY =
    anchorRect.top + halfAnchorHeight - tipSize + tipSpace;
  const bottomY = anchorRect.bottom + gap;
  const topY = anchorRect.top - tooltipRect.height - gap;
  const leftX = anchorRect.left - tooltipRect.width - gap;
  const rightX = anchorRect.right + gap;
  switch (position) {
    case 'bottom':
      offsetX = `${verticalCenterX}px`;
      offsetY = `${bottomY}px`;
      break;
    case 'bottom_end':
      offsetX = `${verticalEndX}px`;
      offsetY = `${bottomY}px`;
      break;
    case 'bottom_start':
      offsetX = `${verticalStartX}px`;
      offsetY = `${bottomY}px`;
      break;
    case 'top':
      offsetX = `${verticalCenterX}px`;
      offsetY = `${topY}px`;
      break;
    case 'top_end':
      offsetX = `${verticalEndX}px`;
      offsetY = `${topY}px`;
      break;
    case 'top_start':
      offsetX = `${verticalStartX}px`;
      offsetY = `${topY}px`;
      break;
    case 'left':
      offsetX = `${leftX}px`;
      offsetY = `${horizontalCenterY}px`;
      break;
    case 'left_end':
      offsetX = `${leftX}px`;
      offsetY = `${horizontalEndY}px`;
      break;
    case 'left_start':
      offsetX = `${leftX}px`;
      offsetY = `${horizontalStartY}px`;
      break;
    case 'right':
      offsetX = `${rightX}px`;
      offsetY = `${horizontalCenterY}px`;
      break;
    case 'right_end':
      offsetX = `${rightX}px`;
      offsetY = `${horizontalEndY}px`;
      break;
    case 'right_start':
      offsetX = `${rightX}px`;
      offsetY = `${horizontalStartY}px`;
      break;
  }

  return { offsetX, offsetY };
}

const SPACE_AVAILABILITY_ORDER: Record<string, Array<string>> = {
  bottom_start: ['top_start', 'bottom', 'bottom_end', 'top', 'top_end'],
  bottom_end: ['top_end', 'bottom', 'bottom_start', 'top', 'top_start'],
  top_start: ['bottom_start', 'top', 'top_end', 'bottom', 'bottom_end'],
  top_end: ['bottom_end', 'top', 'top_start', 'bottom', 'bottom_start'],
  right_end: ['left_end', 'right', 'right_start', 'left', 'left_start'],
  right_start: ['left_start', 'right', 'right_end', 'left', 'left_end'],
  left_end: ['right_end', 'left', 'left_start', 'right', 'right_start'],
  left_start: ['right_start', 'left', 'left_end', 'right', 'right_end'],
  top: ['bottom', 'top_start', 'top_end', 'bottom_start', 'bottom_end'],
  bottom: ['top', 'bottom_start', 'bottom_end', 'top_start', 'top_end'],
  left: ['right', 'left_start', 'left_end', 'right_start', 'right_end'],
  right: ['left', 'right_start', 'right_end', 'left_start', 'left_end']
};

function isTooltipPositionFit(
  position: string,
  anchorRect: DOMRect,
  tooltipRect: DOMRect,
  gap: number
): boolean {
  const { offsetX, offsetY } = calculateOffsets(
    position,
    anchorRect,
    tooltipRect,
    gap
  );

  const positionX = parseInt(offsetX);
  const positionY = parseInt(offsetY);
  const { right, bottom } = tooltipRect;
  const { innerWidth: screenWidth, innerHeight: screenHeight } = window;

  switch (position) {
    case 'top':
      return positionY > 0 && positionX > 0 && right < screenWidth;
    case 'top_start':
      return positionY > 0 && right < screenWidth;
    case 'top_end':
      return positionY > 0 && positionX > 0;
    case 'right_start':
      return bottom < screenHeight && right < screenWidth;
    case 'right_end':
      return positionY > 0 && right < screenWidth;
    case 'right':
      return positionY > 0 && bottom < screenHeight && right < screenWidth;
    case 'bottom_start':
      return bottom < screenHeight && right < screenWidth;
    case 'bottom_end':
      return bottom < screenHeight && positionX > 0;
    case 'bottom':
      return bottom < screenHeight && positionX > 0 && right < screenWidth;
    case 'left_start':
      return bottom < screenHeight && positionX > 0;
    case 'left_end':
      return positionY > 0 && positionX > 0;
    case 'left':
      return bottom < screenHeight && positionY > 0 && positionX > 0;
    default:
      return true;
  }
}

export function getFlippedPosition(
  position: string,
  anchorRect: DOMRect,
  tooltipRect: DOMRect,
  gap: number
): string {
  if (isTooltipPositionFit(position, anchorRect, tooltipRect, gap)) {
    return position;
  }

  const alternativePosition = SPACE_AVAILABILITY_ORDER[position].find(
    (alternativePosition) => {
      return isTooltipPositionFit(
        alternativePosition,
        anchorRect,
        tooltipRect,
        gap
      );
    }
  );

  return alternativePosition || position;
}

export const LONG_PRESS_DELAY = 1000;
export const SUSTAIN_TIME = 3000;

export const getPosition = (
  anchor: HTMLDivElement | null,
  tooltip: HTMLDivElement | null,
  position: TooltipPosition = 'right',
  TIP_OFFSET: string | number = Devices.isTablet() ? 20 : 8,
  isDesktop?: boolean
) => {
  if (anchor && tooltip) {
    const anchorRect = anchor.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const resolvedOffset =
      typeof TIP_OFFSET === 'string'
        ? parseInt(TIP_OFFSET.replace('px', ''))
        : TIP_OFFSET;

    const maxOffset = isDesktop ? 8 : 20;

    const clampedffset = clamp(resolvedOffset, 0, maxOffset);

    const currentPosition = getFlippedPosition(
      position,
      anchorRect,
      tooltipRect,
      clampedffset
    );

    const { offsetX, offsetY } = calculateOffsets(
      currentPosition,
      anchorRect,
      tooltipRect,
      clampedffset
    );
    return {
      offsetX: offsetX.replace('px', ''),
      offsetY: offsetY.replace('px', ''),
      newPosition: currentPosition
    };
  }
  return { offsetX: 0, offsetY: 0 };
};
